import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, FileText, Scale, CheckCircle2, AlertTriangle, Building,
  CreditCard, Lock, FolderArchive, Copyright, Info, ExternalLink,
  Ban, ShieldAlert, UserX, Eye, History, Gavel, Mail, Phone, MapPin, ChevronRight
} from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import SEO from "@/components/SEO";

const SECTIONS = [
  { id: "about", title: "1. About Biz360 Compliance", icon: Building },
  { id: "eligibility", title: "2. Eligibility", icon: CheckCircle2 },
  { id: "scope", title: "3. Scope of Services", icon: FileText },
  { id: "responsibilities", title: "4. Client Responsibilities", icon: Scale },
  { id: "regulatory", title: "5. Government & Regulatory Processing", icon: AlertTriangle },
  { id: "professional", title: "6. Professional Services", icon: ShieldCheck },
  { id: "fees", title: "7. Fees & Payments", icon: CreditCard },
  { id: "portal", title: "8. Client Portal & Account Security", icon: Lock },
  { id: "documents", title: "9. Documents & Digital Records", icon: FolderArchive },
  { id: "ip", title: "10. Intellectual Property", icon: Copyright },
  { id: "website-info", title: "11. Website Information", icon: Info },
  { id: "third-party", title: "12. Third-Party Services", icon: ExternalLink },
  { id: "prohibited", title: "13. Prohibited Use", icon: Ban },
  { id: "liability", title: "14. Limitation of Liability", icon: ShieldAlert },
  { id: "suspension", title: "15. Suspension or Termination", icon: UserX },
  { id: "privacy", title: "16. Privacy", icon: Eye },
  { id: "changes", title: "17. Changes to These Terms", icon: History },
  { id: "jurisdiction", title: "18. Governing Law & Jurisdiction", icon: Gavel },
  { id: "contact", title: "19. Contact Information", icon: Mail },
];

export default function Terms() {
  const [activeSection, setActiveSection] = useState("about");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 140;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const navOffset = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - navOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50 font-sans text-navy-900 antialiased" data-testid="terms-page">
      <SEO
        title="Terms & Conditions | Biz360 Compliance"
        description="Terms and Conditions governing access to and use of Biz360 Compliance website, client portal, registration, and tax advisory services."
        canonical="/terms"
        breadcrumbs={[{ label: "Terms & Conditions", href: "/terms" }]}
      />
      <ScrollProgress />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-14 md:pt-40 md:pb-16 border-b border-slate-200">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-5xl px-5 lg:px-8">
            <Reveal>
              <div className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="terms-breadcrumb">
                <Link to="/" className="hover:text-navy-800 transition-colors">Home</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-navy-700">Terms & Conditions</span>
              </div>
            </Reveal>

            <div className="text-center">
              <Reveal delay={0.05}>
                <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                  Legal & Compliance
                </span>
              </Reveal>

              <Reveal delay={0.1}>
                <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-900 md:text-5xl">
                  TERMS & CONDITIONS
                </h1>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white/80 px-4 py-1.5 text-xs font-medium text-slate-600 shadow-sm border border-slate-200">
                  <History className="h-3.5 w-3.5 text-gold-600" />
                  <span>Last Updated: <strong className="text-navy-900">01/09/2026</strong></span>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-navy-900/10 bg-white/90 p-6 text-left shadow-sm backdrop-blur-sm">
                  <p className="text-base font-semibold text-navy-900">
                    Welcome to Biz360 Compliance.
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    These Terms & Conditions govern your access to and use of the Biz360 Compliance website, client portal, services, digital platforms and related facilities.
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    By accessing our website, submitting an enquiry, registering for an account, purchasing a service or using our client portal, you agree to be bound by these Terms & Conditions.
                  </p>
                  <p className="mt-2 rounded-lg bg-amber-50 p-3 text-xs font-medium text-amber-800 border border-amber-200">
                    If you do not agree with these terms, please do not use the website or our services.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Content Container */}
        <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            
            {/* Quick Navigation Sticky Sidebar */}
            <aside className="hidden lg:col-span-4 lg:block">
              <div className="sticky top-28 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm max-h-[calc(100vh-140px)] overflow-y-auto">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-2">
                  Table of Contents
                </h3>
                <nav className="space-y-1">
                  {SECTIONS.map((sec) => {
                    const SIcon = sec.icon;
                    const isActive = activeSection === sec.id;
                    return (
                      <button
                        key={sec.id}
                        onClick={() => scrollToSection(sec.id)}
                        className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-xs font-medium transition-all ${
                          isActive
                            ? "bg-navy-900 text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                        }`}
                      >
                        <SIcon className={`h-4 w-4 shrink-0 ${isActive ? "text-gold-400" : "text-slate-400"}`} />
                        <span className="truncate">{sec.title}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </aside>

            {/* Terms Articles Content */}
            <div className="space-y-8 lg:col-span-8">

              {/* 1. About Biz360 Compliance */}
              <article id="about" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Building className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">1. About Biz360 Compliance</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Biz360 Compliance is a business-services platform providing support across areas such as:
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Business registration",
                    "GST and taxation",
                    "Accounting and finance",
                    "Statutory compliance",
                    "Audit support",
                    "Business licences and registrations",
                    "Startup and MSME advisory",
                    "Business advisory and related services"
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4 text-xs leading-relaxed text-slate-700">
                  <p className="font-semibold text-navy-900 mb-1">Professional Coordination Note:</p>
                  Certain professional, statutory, certification, audit, legal or regulated activities may require involvement of an appropriately qualified or authorised professional. Where applicable, Biz360 may provide preparation, documentation, coordination or support services and may coordinate with the relevant professional.
                </div>
              </article>

              {/* 2. Eligibility */}
              <article id="eligibility" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <CheckCircle2 className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">2. Eligibility</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  You must provide accurate and complete information when using our services. You represent that:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {[
                    "Information provided by you is accurate to the best of your knowledge.",
                    "You have authority to provide information relating to the business or organisation for which services are requested.",
                    "You will not use the website for unlawful or fraudulent purposes.",
                    "You will not attempt to gain unauthorised access to another user's account or information."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 3. Scope of Services */}
              <article id="scope" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <FileText className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">3. Scope of Services</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  The exact scope of each service will depend upon:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {[
                    "The service selected;",
                    "Information and documents provided by the client;",
                    "Applicable laws and regulations;",
                    "Government or regulatory requirements;",
                    "The agreed commercial terms; and",
                    "Any specific engagement or proposal issued to the client."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs font-medium text-slate-500 italic">
                  A service description on the website is intended to provide general information and does not by itself constitute an unlimited engagement.
                </p>
              </article>

              {/* 4. Client Responsibilities */}
              <article id="responsibilities" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Scale className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">4. Client Responsibilities</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  The client is responsible for providing complete, accurate and timely information and documents.
                </p>
                <p className="mt-3 text-sm font-semibold text-navy-900">Delays caused by:</p>
                <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Missing documents;",
                    "Incorrect information;",
                    "Delayed approvals;",
                    "Delayed payments;",
                    "Government portal issues;",
                    "Third-party system issues; or",
                    "Other circumstances beyond our reasonable control"
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-sm text-slate-700">
                  may affect the timeline for completion.
                </p>
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-xs font-medium text-amber-900">
                  Biz360 shall not be responsible for consequences arising from inaccurate, incomplete or delayed information supplied by the client.
                </div>
              </article>

              {/* 5. Government & Regulatory Processing */}
              <article id="regulatory" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <AlertTriangle className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">5. Government & Regulatory Processing</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Where a service involves a government department, statutory authority, bank, financial institution, regulatory body or third-party platform, the final approval, registration, sanction, refund, certificate or other outcome may depend upon the decision of that authority.
                </p>
                <p className="mt-3 text-sm font-semibold text-navy-900">Biz360 does not guarantee:</p>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Government approval",
                    "Tax refund",
                    "Loan sanction",
                    "Subsidy sanction",
                    "Registration approval",
                    "DPIIT recognition",
                    "Licence approval",
                    "Any other outcome controlled by an external authority"
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 rounded-lg bg-slate-50 p-2.5 border border-slate-200/70">
                      <span className="h-2 w-2 rounded-full bg-rose-500 shrink-0" />
                      <span className="text-xs font-medium text-slate-800">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 6. Professional Services */}
              <article id="professional" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">6. Professional Services</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Where applicable law requires a service, certification, audit opinion, legal representation or other professional function to be performed by an appropriately authorised professional, such function shall be undertaken by the relevant professional.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">
                  Biz360's role may include preparation, documentation, accounting support, reconciliation, coordination, analysis and other agreed support activities.
                </p>
              </article>

              {/* 7. Fees & Payments */}
              <article id="fees" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <CreditCard className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">7. Fees & Payments</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Service fees will be communicated before commencement wherever reasonably practicable.
                </p>
                <p className="mt-3 text-sm font-semibold text-navy-900">Unless otherwise agreed:</p>
                <ul className="mt-2 space-y-2 text-sm text-slate-700">
                  {[
                    "Government fees and statutory charges are additional.",
                    "Third-party charges are additional.",
                    "Taxes applicable to the service are additional.",
                    "Additional work outside the agreed scope may attract additional fees."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-sm text-slate-700 font-medium">
                  Payment terms stated in the quotation, proposal, invoice or engagement communication shall apply.
                </p>
              </article>

              {/* 8. Client Portal & Account Security */}
              <article id="portal" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Lock className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">8. Client Portal & Account Security</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Where a client portal is provided, users are responsible for maintaining the confidentiality of their login credentials.
                </p>
                <p className="mt-3 text-sm font-semibold text-navy-900">Users must immediately inform Biz360 if they suspect:</p>
                <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Unauthorised access;",
                    "Loss of credentials;",
                    "Account compromise; or",
                    "Unauthorised use of their account."
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-navy-800 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50/70 p-3.5 text-xs font-semibold text-rose-900">
                  Users must not share their login credentials with unauthorised persons.
                </div>
              </article>

              {/* 9. Documents & Digital Records */}
              <article id="documents" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <FolderArchive className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">9. Documents & Digital Records</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Documents submitted through the website or client portal may be stored electronically for providing services, maintaining records, compliance, dispute resolution, security and other legitimate purposes.
                </p>
                <p className="mt-3 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  Clients should not upload documents that are unrelated to the requested service.
                </p>
              </article>

              {/* 10. Intellectual Property */}
              <article id="ip" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Copyright className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">10. Intellectual Property</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Unless otherwise stated, the website, design, content, logos, graphics, software, workflows, templates and other materials provided by Biz360 are owned by or licensed to Biz360 and may not be reproduced, modified, distributed or commercially exploited without prior written permission.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">
                  Client-specific documents remain the property of the client or the respective rights holder, subject to any rights required for providing the services.
                </p>
              </article>

              {/* 11. Website Information */}
              <article id="website-info" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Info className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">11. Website Information</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Information published on the website is intended for general informational purposes.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  Tax laws, regulations, government schemes, fees, deadlines and procedures may change.
                </p>
                <p className="mt-2 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  Information should not be treated as personalised professional advice unless specifically provided as part of an engagement.
                </p>
              </article>

              {/* 12. Third-Party Services */}
              <article id="third-party" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ExternalLink className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">12. Third-Party Services</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  The website or services may use or link to third-party platforms, including:
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Government portals",
                    "Payment gateways",
                    "Communication platforms",
                    "Cloud services",
                    "Accounting or business software",
                    "Other service providers"
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-slate-500 italic">
                  Biz360 is not responsible for the independent operation, availability or policies of third-party platforms.
                </p>
              </article>

              {/* 13. Prohibited Use */}
              <article id="prohibited" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Ban className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">13. Prohibited Use</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700 font-semibold">
                  Users shall not:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {[
                    "Attempt unauthorised access to the website or portal;",
                    "Introduce malicious software;",
                    "Copy or misuse website content;",
                    "Use the platform for fraudulent activity;",
                    "Impersonate another person or organisation;",
                    "Interfere with website operations; or",
                    "Use the platform for any unlawful purpose."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 14. Limitation of Liability */}
              <article id="liability" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ShieldAlert className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">14. Limitation of Liability</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Biz360 shall exercise reasonable care in providing agreed services. However, Biz360 shall not be liable for losses arising solely from:
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Government or regulatory decisions;",
                    "Changes in law;",
                    "Portal downtime;",
                    "Third-party service failures;",
                    "Force majeure events;",
                    "Client-provided incorrect information;",
                    "Client delays; or",
                    "Events outside our reasonable control."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  Nothing in these terms shall exclude liability that cannot lawfully be excluded.
                </p>
              </article>

              {/* 15. Suspension or Termination */}
              <article id="suspension" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <UserX className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">15. Suspension or Termination</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Biz360 may suspend or terminate access to the website, client portal or services where:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {[
                    "Fees remain unpaid;",
                    "False or fraudulent information is provided;",
                    "The platform is misused;",
                    "Security is compromised; or",
                    "Continued service would violate applicable law."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 16. Privacy */}
              <article id="privacy" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Eye className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">16. Privacy</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Personal information is handled in accordance with our Privacy Policy and applicable data-protection requirements.
                </p>
              </article>

              {/* 17. Changes to These Terms */}
              <article id="changes" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <History className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">17. Changes to These Terms</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Biz360 may update these Terms & Conditions from time to time. The revised version will be published on the website with the updated date.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  Continued use of the website after an update may constitute acceptance of the revised terms, subject to applicable law.
                </p>
              </article>

              {/* 18. Governing Law & Jurisdiction */}
              <article id="jurisdiction" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Gavel className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">18. Governing Law & Jurisdiction</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  These Terms & Conditions shall be governed by the laws of India.
                </p>
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs leading-relaxed text-slate-800">
                  Subject to applicable law and any agreed dispute-resolution mechanism, courts having appropriate jurisdiction at <strong>Pune, Maharashtra</strong> shall have jurisdiction.
                </div>
              </article>

              {/* 19. Contact */}
              <article id="contact" className="rounded-2xl border border-gold-300/60 bg-gradient-to-br from-white to-gold-50/20 p-6 md:p-8 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gold-600 text-white shadow-sm">
                    <Mail className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">19. Contact Information</h2>
                </div>
                <p className="mt-3 text-sm text-slate-700">
                  For questions regarding these Terms & Conditions:
                </p>
                <div className="mt-5 space-y-4 rounded-xl border border-navy-900/10 bg-white p-5 shadow-sm">
                  <p className="text-base font-bold text-navy-900">Biz360 Compliance</p>
                  
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <Mail className="h-4 w-4 text-gold-600 shrink-0" />
                    <span>Email: <a href="mailto:biz360compliance@gmail.com" className="font-semibold text-navy-900 hover:text-gold-600 transition-colors">biz360compliance@gmail.com</a></span>
                  </div>

                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <Phone className="h-4 w-4 text-gold-600 shrink-0" />
                    <span>Phone: <a href="tel:9067368357" className="font-semibold text-navy-900 hover:text-gold-600 transition-colors">9067368357</a></span>
                  </div>

                  <div className="flex items-start gap-3 text-sm text-slate-700">
                    <MapPin className="h-4 w-4 text-gold-600 shrink-0 mt-0.5" />
                    <span>Address: <strong>Ofc No. 70, 4th Floor, Patil Arcade, Above Kalinga Hotel, Khilare Road, Erandwane, Pune 411004.</strong></span>
                  </div>
                </div>
              </article>

            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
